import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Calendar, User, ChevronRight, Clock,
  Brain, FileText, MessageCircle, Heart, BookOpen,
  Copy, Check
} from 'lucide-react';
import { Breadcrumbs, blogPaths } from '@/Breadcrumbs';
import {
  CATEGORIES, SITE, articlesFor, articleRoute, translationFor, relatedFor,
  articleCanonical, articleSocialImage
} from '@/content/articles';

const categoryIcons = {
  'psihologie-psihoterapie': Brain,
  'teme-terapeutice': Brain,
  'despre-terapie': MessageCircle,
  'sanatate-mentala': Heart,
  'recomandari': BookOpen,
};

const categoryName = (id, language) => CATEGORIES[id]?.[language] || CATEGORIES[id]?.ro || id;

const formatDate = (value, language) =>
  value
    ? new Date(value).toLocaleDateString(language === 'ro' ? 'ro-RO' : 'en-US', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : '';

const absolute = (url) => (!url ? `${SITE}/og-default.jpg` : /^https?:/.test(url) ? url : SITE + url);

/* ---------- Markdown -> HTML (H1 reserved for the article title) ---------- */

const renderInline = (text) =>
  text
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
    .replace(/(^|[^*])\*(?!\s)(.+?)\*/g, '$1<em>$2</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label, href) =>
      `<a href="${href}" class="text-sage underline underline-offset-4 hover:text-sage-dark"${
        /^https?:/.test(href) ? ' target="_blank" rel="noopener noreferrer"' : ''
      }>${label}</a>`);

const slugify = (text) =>
  text
    .replace(/<[^>]+>/g, '')
    .replace(/\*\*/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/* Renders the article markdown and returns the intro (everything before the first H2),
 * the rest of the body and the H2 inventory used for the table of contents. */
export const renderMarkdown = (md) => {
  const lines = (md || '').replace(/\r\n/g, '\n').split('\n');
  const intro = [];
  const body = [];
  const headings = [];
  let para = [];
  let list = null;
  let table = null;
  let details = null;
  let seenH2 = false;

  const push = (html) => (seenH2 ? body : intro).push(html);

  const flushPara = () => {
    if (!para.length) return;
    push(`<p class="mb-5 text-[17.5px] leading-[1.8] text-sage-900/80">${renderInline(para.join(' '))}</p>`);
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    const cls = list.tag === 'ul' ? 'list-disc' : 'list-decimal';
    push(
      `<${list.tag} class="${cls} pl-6 mb-5 space-y-2 text-[17.5px] leading-[1.8] text-sage-900/80">` +
        list.items.map((i) => `<li>${renderInline(i)}</li>`).join('') +
        `</${list.tag}>`
    );
    list = null;
  };
  const flushTable = () => {
    if (!table) return;
    const [head, ...rows] = table;
    push(
      '<div class="my-8 overflow-x-auto rounded-2xl border border-[#E5E1D8]">' +
        '<table class="w-full min-w-[34rem] border-collapse text-left text-[15px]">' +
        '<thead><tr>' +
        head
          .map(
            (cell) =>
              `<th scope="col" class="bg-[#F5F2EA] border-b border-[#E5E1D8] px-4 py-3 font-medium text-sage-900">${renderInline(cell)}</th>`
          )
          .join('') +
        '</tr></thead><tbody>' +
        rows
          .map(
            (row) =>
              '<tr class="align-top">' +
              row
                .map(
                  (cell) =>
                    `<td class="border-b border-[#EDE9DF] px-4 py-3.5 leading-[1.65] text-sage-900/80">${renderInline(cell)}</td>`
                )
                .join('') +
              '</tr>'
          )
          .join('') +
        '</tbody></table></div>'
    );
    table = null;
  };

  const flushDetails = () => {
    if (!details) return;
    const inner = renderMarkdown(details.lines.join('\n'));
    push(
      '<details class="my-7 rounded-2xl border border-[#E5E1D8] bg-[#FDFBF7] px-5 py-4 md:px-6">' +
        `<summary class="cursor-pointer text-[14.5px] font-medium text-sage-900/70 hover:text-sage transition-colors">${renderInline(details.label)}</summary>` +
        `<div class="mt-4">${[inner.intro, inner.body].filter(Boolean).join('\n')}</div>` +
        '</details>'
    );
    details = null;
  };

  for (const raw of lines) {
    const line = raw.trim();

    if (line.startsWith(':::')) {
      if (details) { flushDetails(); continue; }
      flushPara(); flushList(); flushTable();
      details = { label: line.replace(/^:::\s*(details)?\s*/, ''), lines: [] };
      continue;
    }
    if (details) { details.lines.push(raw); continue; }

    if (!line) { flushPara(); flushList(); flushTable(); continue; }

    // ![alt](/path/name-1600.webp "intrinsicWidthxintrinsicHeight")
    const image = line.match(/^!\[([^\]]*)\]\(([^\s)]+)(?:\s+"(\d+)x(\d+)")?\)$/);
    if (image) {
      flushPara(); flushList(); flushTable();
      const [, alt, src, w, h] = image;
      const srcSet = /-\d+\.webp$/.test(src)
        ? [640, 960, 1280, 1600].map((v) => `${src.replace(/-\d+\.webp$/, `-${v}.webp`)} ${v}w`).join(', ')
        : '';
      push(
        '<figure class="my-8 overflow-hidden rounded-2xl border border-[#E5E1D8]">' +
          `<img src="${src}"${srcSet ? ` srcset="${srcSet}" sizes="(max-width: 768px) 100vw, 800px"` : ''}` +
          ` alt="${alt.replace(/"/g, '&quot;')}"${w ? ` width="${w}" height="${h}"` : ''}` +
          ' loading="lazy" decoding="async" class="w-full h-auto" />' +
          '</figure>'
      );
      continue;
    }

    if (line.startsWith('|')) {
      flushPara(); flushList();
      const cells = line.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
      if (cells.every((c) => /^:?-{2,}:?$/.test(c))) continue; // separator row
      (table = table || []).push(cells);
      continue;
    }
    flushTable();

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      flushPara(); flushList();
      // '#' and '##' render as H2 (H1 belongs to the title); '###'+ render as H3,
      // promoted to H2 when no H2 has appeared yet so no heading level is skipped.
      let level = heading[1].length <= 2 ? 2 : 3;
      if (level === 3 && !seenH2) level = 2;
      const text = heading[2];
      const id = slugify(text);
      if (level === 2) {
        seenH2 = true;
        headings.push({ id, text: text.replace(/\*\*/g, '') });
      }
      const cls = level === 2
        ? 'font-serif text-[1.6rem] md:text-[1.9rem] text-sage-900 mt-10 mb-4 leading-snug scroll-mt-28'
        : 'font-serif text-[1.25rem] md:text-[1.4rem] text-sage-900 mt-7 mb-3 leading-snug scroll-mt-28';
      push(`<h${level} id="${id}" class="${cls}">${renderInline(text)}</h${level}>`);
      continue;
    }

    if (line.startsWith('> ')) {
      flushPara(); flushList();
      push(
        `<blockquote class="border-l-2 border-sage/40 pl-5 my-6 italic text-sage-900/75">${renderInline(line.slice(2))}</blockquote>`
      );
      continue;
    }

    const ul = line.match(/^[-*]\s+(.*)$/);
    const ol = line.match(/^\d+[.)]\s+(.*)$/);
    if (ul || ol) {
      flushPara();
      const tag = ul ? 'ul' : 'ol';
      if (!list || list.tag !== tag) { flushList(); list = { tag, items: [] }; }
      list.items.push((ul || ol)[1]);
      continue;
    }

    flushList();
    para.push(line);
  }
  flushPara(); flushList(); flushTable(); flushDetails();
  return { intro: intro.join('\n'), body: body.join('\n'), headings };
};

export const markdownToHtml = (md) => {
  const { intro, body } = renderMarkdown(md);
  return [intro, body].filter(Boolean).join('\n');
};

const PORTRAIT =
  'https://static.prod-images.emergentagent.com/jobs/82b2486c-e00d-4241-8cfe-e4828bece8d5/images/9f9add361802412c0256b640fbedc5e66e942513a3d0734958420b8129b8e203.jpeg';

const AUTHOR = {
  ro: {
    byline: 'De',
    name: 'Ruxandra Șterbeț',
    jobTitle: 'Psiholog clinician și psihoterapeut integrativ',
    aboutHref: '/despre-mine',
    aboutLabel: 'Despre Ruxandra',
    cardHeading: 'Despre autor',
    bio: 'Ruxandra Șterbeț este psiholog și psihoterapeut integrativ, cu activitate în domeniul psihologiei clinice, consilierii și psihoterapiei. Este doctorandă în psihologie și îmbină în practica sa formarea academică, experiența profesională și o abordare fundamentată pe cunoștințe psihologice actuale.',
    contents: 'Cuprins',
    readTime: (m) => `${m} min de lectură`,
    portraitAlt: 'Ruxandra Șterbeț, psiholog și psihoterapeut',
  },
  en: {
    byline: 'By',
    name: 'Ruxandra Șterbeț',
    jobTitle: 'Clinical psychologist and integrative psychotherapist',
    aboutHref: '/about-me',
    aboutLabel: 'About Ruxandra',
    cardHeading: 'About the Author',
    bio: 'Ruxandra Șterbeț is a psychologist and integrative psychotherapist working across clinical psychology, psychological counselling and psychotherapy. She is also a doctoral researcher in psychology, integrating academic training, professional practice and evidence-informed psychological knowledge in her work.',
    contents: 'Contents',
    readTime: (m) => `${m} min read`,
    portraitAlt: 'Ruxandra Șterbeț, psychologist and psychotherapist',
  },
};

const LINKEDIN_URL = 'https://www.linkedin.com/in/ruxandra-sterbet-09b43b17a/';
const CALENDLY_URL = 'https://calendly.com/ruxandrasterbet/therapy-session';

/* ---------- Reading progress indicator ---------- */

const ReadingProgress = () => {
  const [progress, setProgress] = useState(0);
  const reducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  );
  useEffect(() => {
    const update = () => {
      const doc = document.documentElement;
      const total = (doc.scrollHeight || 0) - (doc.clientHeight || 0);
      if (total <= 0) return setProgress(0);
      setProgress(Math.min(100, Math.max(0, (window.scrollY / total) * 100)));
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  return (
    <div
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 h-[3px] z-50 pointer-events-none"
      data-testid="article-reading-progress"
    >
      <div
        className="h-full bg-sage"
        style={{
          width: `${progress}%`,
          transition: reducedMotion.current ? 'none' : 'width 90ms linear',
        }}
      />
    </div>
  );
};

/* ---------- Promotional offer card ---------- */

/** Compute offer status against Europe/Chisinau wall-clock time. */
const offerStatus = (offer) => {
  if (!offer || !offer.startsAt || !offer.endsAt) return 'active';
  try {
    const now = new Date();
    // approximate Europe/Chisinau wall-clock date (EET/EEST, UTC+2/+3). Use locale date parts.
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Chisinau',
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(now).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const today = `${parts.year}-${parts.month}-${parts.day}`;
    if (today < offer.startsAt) return 'upcoming';
    if (today > offer.endsAt) return 'ended';
    return 'active';
  } catch {
    return 'active';
  }
};

export const OfferCard = ({ offer, language = 'ro', variant = 'intro' }) => {
  const [copied, setCopied] = useState(false);
  if (!offer || !offer.code) return null;
  const status = offerStatus(offer);
  const l = offer.labels || {};
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(offer.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      /* clipboard permission denied — the code stays visible for manual copy */
    }
  };
  const chip =
    status === 'upcoming' ? l.statusUpcoming :
    status === 'ended'    ? l.statusEnded    :
                            l.statusActive;
  const headline = status === 'ended' ? (l.endedHeadline || l.headline) : l.headline;
  const description = status === 'ended' ? (l.endedDescription || l.description) : l.description;
  const showCode = status !== 'ended';

  // Try to split "15% off · 10–31 October 2026" into (discount phrase, date window)
  const parts = (headline || '').split(/\s[·•]\s/);
  const discountPhrase = parts[0] || headline || '';
  const dateWindow = parts.slice(1).join(' · ');
  const pctMatch = discountPhrase.match(/^(\d+)%/);
  const pctNumber = pctMatch ? pctMatch[1] : null;
  const discountCaption = pctMatch ? discountPhrase.slice(pctMatch[0].length).trim() : discountPhrase;

  const dotTone =
    status === 'active'   ? 'bg-emerald-500' :
    status === 'upcoming' ? 'bg-sage'        :
                            'bg-sage-900/30';

  return (
    <aside
      data-testid={`article-offer-${variant}`}
      aria-label={l.ariaLabel || l.headline}
      className={`${variant === 'intro' ? 'my-10' : 'my-12'} relative overflow-hidden rounded-3xl border border-sage/25 bg-[#FDFBF7] shadow-[0_1px_0_rgba(107,135,110,0.06),0_18px_40px_-28px_rgba(60,80,65,0.35)]`}
    >
      {/* decorative sage sprig */}
      <svg
        aria-hidden="true"
        viewBox="0 0 240 240"
        className="pointer-events-none absolute -right-6 -top-6 h-40 w-40 text-sage/35 md:-right-4 md:-top-4 md:h-56 md:w-56"
      >
        <g fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
          <path d="M60,210 C80,150 110,110 170,70" />
          <path d="M98,160 Q78,150 70,135 Q92,138 108,150 Z" fill="currentColor" opacity="0.45" />
          <path d="M118,132 Q98,124 92,108 Q114,112 128,124 Z" fill="currentColor" opacity="0.5" />
          <path d="M140,106 Q122,96 118,80 Q140,86 152,100 Z" fill="currentColor" opacity="0.55" />
          <path d="M162,82 Q146,70 144,54 Q166,60 176,76 Z" fill="currentColor" opacity="0.6" />
          <circle cx="170" cy="70" r="4" fill="currentColor" opacity="0.7" />
        </g>
      </svg>
      {/* sage top accent */}
      <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-sage/50 via-sage/25 to-transparent" />

      <div className="relative grid gap-8 p-7 md:p-9 lg:grid-cols-5 lg:gap-10">
        {/* LEFT: narrative column */}
        <div className="lg:col-span-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-sage/30 bg-white/70 px-3 py-1 text-[11.5px] font-medium uppercase tracking-[0.14em] text-sage-900/80">
              <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${dotTone}`}>
                {status === 'active' && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70 motion-safe:animate-ping"
                  />
                )}
              </span>
              {chip}
            </span>
            {dateWindow && status !== 'ended' && (
              <span className="text-[12px] text-sage-900/70">{l.window}</span>
            )}
          </div>

          <div className="mt-5 flex items-end gap-4 md:gap-5">
            {pctNumber && (
              <div
                aria-hidden="true"
                className="flex items-start font-serif leading-[0.9] text-sage-dark"
              >
                <span className="text-[3.4rem] md:text-[4rem]">{pctNumber}</span>
                <span className="text-[1.6rem] md:text-[1.9rem] mt-1 md:mt-1.5 text-sage-dark/80">%</span>
              </div>
            )}
            <div className="pb-1.5 md:pb-2">
              <h2 className="font-serif text-[1.3rem] md:text-[1.55rem] leading-tight text-sage-900">
                {discountCaption}
              </h2>
              {dateWindow && (
                <p className="mt-1 text-[13.5px] text-sage-900/70">{dateWindow}</p>
              )}
            </div>
          </div>

          <p className="mt-5 text-[15.5px] leading-[1.75] text-sage-900/80 max-w-[52ch]">
            {description}
          </p>

          {l.footnote && status !== 'ended' && (
            <p className="mt-5 border-t border-sage/15 pt-4 text-[13px] leading-[1.7] text-sage-900/70 max-w-[52ch]">
              {l.footnote}
            </p>
          )}
        </div>

        {/* RIGHT: coupon + CTA */}
        <div className="lg:col-span-2 lg:border-l lg:border-dashed lg:border-sage/30 lg:pl-10">
          {showCode && (
            <>
              <div className="text-[11.5px] font-medium uppercase tracking-[0.18em] text-sage-900/70">
                {l.codeLabel || (language === 'ro' ? 'Cod promoțional' : 'Promo code')}
              </div>
              <div
                className="relative mt-3 rounded-2xl border-2 border-dashed border-sage/40 bg-white/60 px-5 py-5 text-center"
                data-testid={`offer-code-wrap-${variant}`}
              >
                <span
                  aria-hidden="true"
                  className="absolute -left-[9px] top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-[#FDFBF7] border border-sage/25"
                />
                <span
                  aria-hidden="true"
                  className="absolute -right-[9px] top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-[#FDFBF7] border border-sage/25"
                />
                <code
                  data-testid={`offer-code-${variant}`}
                  className="block font-mono text-[1.1rem] font-semibold tracking-[0.22em] text-sage-900"
                >
                  {offer.code}
                </code>
              </div>
              <button
                type="button"
                data-testid={`offer-copy-${variant}`}
                onClick={copy}
                aria-label={copied ? (l.copied || 'Copied') : (l.copy || 'Copy code')}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-sage/30 px-3.5 py-1.5 text-[13px] font-medium text-sage-900/80 transition-colors hover:border-sage hover:text-sage focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/50"
              >
                {copied ? <Check size={14} strokeWidth={2.1} /> : <Copy size={14} strokeWidth={1.9} />}
                <span aria-live="polite">{copied ? (l.copied || 'Copied') : (l.copy || 'Copy code')}</span>
              </button>
            </>
          )}
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`offer-book-${variant}`}
            className={`${showCode ? 'mt-6' : ''} inline-flex w-full items-center justify-center gap-2 rounded-full bg-warm-orange px-5 py-3 text-[14.5px] font-medium text-white transition-colors hover:bg-warm-orangeDark focus:outline-none focus-visible:ring-2 focus-visible:ring-warm-orange/60`}
          >
            {l.cta}
            <ChevronRight size={16} strokeWidth={2} />
          </a>
        </div>
      </div>
    </aside>
  );
};

/* ---------- document head for a static article ---------- */

// Same Person entity as the site-wide @graph (public/index.html), never a duplicate
const authorEntity = (language) => ({
  '@type': 'Person',
  '@id': `${SITE}/#person`,
  name: AUTHOR[language === 'en' ? 'en' : 'ro'].name,
  url: SITE + AUTHOR[language === 'en' ? 'en' : 'ro'].aboutHref,
  image: PORTRAIT,
  jobTitle: AUTHOR[language === 'en' ? 'en' : 'ro'].jobTitle,
  sameAs: [LINKEDIN_URL],
});

const upsert = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) { el = create(); el.setAttribute('data-article-meta', 'true'); document.head.appendChild(el); }
  return el;
};
const setProp = (property, content) =>
  upsert(`meta[property="${property}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute('property', property);
    return m;
  }).setAttribute('content', content);
const setName = (name, content) =>
  upsert(`meta[name="${name}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute('name', name);
    return m;
  }).setAttribute('content', content);

const applyArticleHead = (article) => {
  const translation = translationFor(article);
  const url = articleCanonical(article);
  const title = article.seoTitle || article.title;
  const description = article.metaDescription || article.excerpt;
  const image = absolute(articleSocialImage(article));
  const imageAlt = article.imageAlt || article.title;

  document.title = title;
  document.documentElement.setAttribute('lang', article.language);
  setName('description', description);
  upsert('link[rel="canonical"]', () => {
    const l = document.createElement('link');
    l.setAttribute('rel', 'canonical');
    return l;
  }).setAttribute('href', url);

  setProp('og:type', 'article');
  setProp('og:title', title);
  setProp('og:description', description);
  setProp('og:url', url);
  setProp('og:image', image);
  setProp('og:image:alt', imageAlt);
  setProp('article:published_time', article.publishedAt || '');
  if (article.modifiedAt) setProp('article:modified_time', article.modifiedAt);
  setName('twitter:card', 'summary_large_image');
  setName('twitter:title', title);
  setName('twitter:description', description);
  setName('twitter:image', image);
  setName('twitter:image:alt', imageAlt);

  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  if (translation) {
    for (const item of [article, translation]) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', item.language);
      link.setAttribute('href', articleCanonical(item));
      link.setAttribute('data-article-meta', 'true');
      document.head.appendChild(link);
    }
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    headline: article.title,
    description,
    inLanguage: article.language,
    datePublished: article.publishedAt,
    ...(article.modifiedAt ? { dateModified: article.modifiedAt } : {}),
    author: authorEntity(article.language),
    publisher: { '@type': 'Organization', '@id': `${SITE}/#practice`, name: 'Ruxandra Sterbet', logo: { '@type': 'ImageObject', url: `${SITE}/icon-512.png` } },
    image: [image],
    articleSection: categoryName(article.category, article.language),
    ...(article.keywords?.length ? { keywords: article.keywords.join(', ') } : {}),
  };
  let script = document.getElementById('article-jsonld');
  if (!script) {
    script = document.createElement('script');
    script.id = 'article-jsonld';
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(jsonLd);

  return () => {
    document.head.querySelectorAll('meta[data-article-meta="true"], link[data-article-meta="true"]').forEach((el) => el.remove());
    document.getElementById('article-jsonld')?.remove();
    const ogType = document.head.querySelector('meta[property="og:type"]');
    if (ogType) ogType.setAttribute('content', 'website');
    ['article:published_time', 'article:modified_time'].forEach((p) => {
      document.head.querySelector(`meta[property="${p}"]`)?.remove();
    });
  };
};

/* ---------- Article card ---------- */

export const ArticleCard = ({ article, language = 'ro', onClick, headingLevel = 'h3' }) => {
  const Icon = categoryIcons[article.category] || FileText;
  const Heading = headingLevel === 'h2' ? 'h2' : 'h3';
  return (
    <a
      href={articleRoute(article)}
      data-testid={`article-card-${article.slug}`}
      onClick={(e) => { if (onClick) { e.preventDefault(); onClick(); } }}
      className="block"
    >
      <article className="h-full bg-white rounded-2xl overflow-hidden border border-stone-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
        {article.coverImage && (
          <div className="aspect-video overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.imageAlt || article.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        )}
        <div className="p-6">
          <div className="flex items-center gap-2 text-sm text-sage mb-3">
            <Icon size={14} strokeWidth={1.5} />
            <span>{categoryName(article.category, language)}</span>
          </div>
          <Heading className="font-serif text-xl text-sage-900 mb-2 group-hover:text-sage transition-colors">
            {article.title}
          </Heading>
          <p className="text-sage-900/70 text-sm line-clamp-3 mb-4">{article.excerpt}</p>
          <div className="flex items-center justify-between text-xs text-sage-900/70">
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              {formatDate(article.publishedAt, language)}
            </span>
            <span className="flex items-center gap-1 text-sage group-hover:translate-x-1 transition-transform">
              {language === 'ro' ? 'Citește articolul' : 'Read article'} <ChevronRight size={14} />
            </span>
          </div>
        </div>
      </article>
    </a>
  );
};

/* ---------- Listing page (only rendered when genuine articles exist) ---------- */

export const ArticlesPage = ({ t, language = 'ro', onSelectArticle, onNavigate }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const all = useMemo(() => articlesFor(language), [language]);
  const categories = useMemo(() => [...new Set(all.map((a) => a.category))], [all]);
  const articles = selectedCategory ? all.filter((a) => a.category === selectedCategory) : all;
  const paths = blogPaths(language);

  return (
    <div className="min-h-screen bg-cream pt-24 md:pt-28 pb-16 md:pb-20" data-testid="articles-page">
      <div className="max-w-[1240px] mx-auto px-6 md:px-12">
        <Breadcrumbs
          className="mb-9 md:mb-12"
          items={[
            { label: t.breadcrumbHome, href: paths.home },
            { label: t.navBlog, href: paths.blog },
            { label: t.navBlogArticles },
          ]}
        />

        <div className="max-w-[44rem] mb-12 md:mb-16">
          <span className="block text-sage-900/70 text-xs font-medium tracking-[0.18em] uppercase mb-4">
            {t.navBlog}
          </span>
          <h1 data-testid="articles-title" className="font-serif text-4xl sm:text-5xl text-sage-900 leading-[1.1] mb-6">
            {t.navBlogArticles}
          </h1>
          <p className="text-sage-900/75 text-[16.5px] md:text-[17.5px] leading-[1.8]">
            {t.articlesArchiveIntro}
          </p>
        </div>

        {categories.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              data-testid="articles-filter-all"
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                !selectedCategory ? 'bg-sage text-white' : 'bg-white text-sage-900 hover:bg-sage/10 border border-stone-200'
              }`}
            >
              {language === 'ro' ? 'Toate' : 'All'}
            </button>
            {categories.map((cat) => {
              const Icon = categoryIcons[cat] || FileText;
              return (
                <button
                  key={cat}
                  data-testid={`articles-filter-${cat}`}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                    selectedCategory === cat ? 'bg-sage text-white' : 'bg-white text-sage-900 hover:bg-sage/10 border border-stone-200'
                  }`}
                >
                  <Icon size={14} strokeWidth={1.5} />
                  {categoryName(cat, language)}
                </button>
              );
            })}
          </div>
        )}

        {articles.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {articles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                language={language}
                headingLevel="h2"
                onClick={onSelectArticle ? () => onSelectArticle(article) : undefined}
              />
            ))}
          </div>
        ) : (
          <div
            data-testid="articles-empty-state"
            className="rounded-2xl border border-dashed border-[#E5E1D8] bg-[#FDFBF7] px-7 py-10 md:px-12 md:py-14 max-w-[52rem]"
          >
            <span className="w-12 h-12 rounded-full bg-[#E7EFE4] flex items-center justify-center mb-6">
              <BookOpen size={22} strokeWidth={1.5} className="text-[#6F8A72]" />
            </span>
            <h2 className="font-serif text-2xl md:text-[1.75rem] text-sage-900 leading-snug mb-3">
              {t.articlesEmptyTitle}
            </h2>
            <p className="text-sage-900/70 text-[15.5px] leading-[1.8] max-w-[58ch] mb-7">
              {t.articlesEmptyText}
            </p>
            <a
              data-testid="articles-empty-cta"
              href={paths.resources}
              onClick={(e) => {
                if (onNavigate) {
                  e.preventDefault();
                  onNavigate('resources');
                }
              }}
              className="inline-flex items-center gap-2 text-[15px] font-medium text-[#A4552F] hover:text-[#8F5236] transition-colors"
            >
              {t.articlesEmptyCta}
              <ChevronRight size={16} strokeWidth={1.9} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------- Single static article ---------- */

export const ArticlePage = ({ article, language = 'ro', onSelectArticle }) => {
  const Icon = categoryIcons[article.category] || FileText;
  const lang = article.language === 'en' ? 'en' : 'ro';
  const a = AUTHOR[lang];
  const { intro, body, headings } = useMemo(() => renderMarkdown(article.content), [article]);
  const related = useMemo(() => relatedFor(article), [article]);
  const minutes = useMemo(
    () => Math.max(1, Math.round((article.content || '').trim().split(/\s+/).length / 200)),
    [article]
  );
  const paths = blogPaths(lang);
  const srcSet = (article.coverWidths || [])
    .map((w) => `${article.coverImage.replace(/-\d+\.webp$/, `-${w}.webp`)} ${w}w`)
    .join(', ');

  useEffect(() => applyArticleHead(article), [article]);

  return (
    <div className="min-h-screen bg-cream pt-24 md:pt-28 pb-16 md:pb-20" data-testid="article-page">
      <ReadingProgress />
      <article className="max-w-5xl mx-auto px-6 md:px-12">
        <Breadcrumbs
          className="mb-9 md:mb-10"
          items={[
            { label: lang === 'ro' ? 'Acasă' : 'Home', href: paths.home },
            { label: 'Blog', href: paths.blog },
            { label: lang === 'ro' ? 'Articole' : 'Articles', href: paths.articles },
            { label: article.title },
          ]}
        />

        <header className="mb-8">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[13.5px] mb-5">
            <span className="flex items-center gap-1.5 bg-sage/10 text-sage px-3 py-1 rounded-full">
              <Icon size={14} strokeWidth={1.5} />
              {categoryName(article.category, lang)}
            </span>
            <time dateTime={article.publishedAt} className="flex items-center gap-1.5 text-sage-900/70">
              <Calendar size={14} strokeWidth={1.6} />
              {formatDate(article.publishedAt, lang)}
            </time>
            <span aria-hidden="true" className="text-sage-900/25">·</span>
            <span className="flex items-center gap-1.5 text-sage-900/70">
              <Clock size={14} strokeWidth={1.6} />
              {a.readTime(minutes)}
            </span>
          </div>

          <h1 className="font-serif text-[1.85rem] sm:text-[2.35rem] md:text-[2.7rem] text-sage-900 mb-4 leading-[1.15]">
            {article.title}
          </h1>

          <p data-testid="article-byline" className="flex items-center gap-2 text-[14.5px] text-sage-900/70">
            <User size={15} strokeWidth={1.5} className="text-sage-900/70" />
            {a.byline}{' '}
            <a
              data-testid="article-byline-link"
              href={a.aboutHref}
              className="text-sage-900/80 underline decoration-sage-900/25 underline-offset-4 hover:text-sage hover:decoration-sage/50 transition-colors"
            >
              {a.name}
            </a>
          </p>
        </header>

        {article.offer && <OfferCard offer={article.offer} language={lang} variant="intro" />}

        {article.coverImage && (
          <figure className="mb-9 rounded-2xl overflow-hidden border border-[#E5E1D8]">
            <img
              data-testid="article-cover"
              src={article.coverImage}
              {...(srcSet ? { srcSet, sizes: '(max-width: 768px) 100vw, 800px' } : {})}
              alt={article.imageAlt || article.title}
              width={article.coverWidth || 1600}
              height={article.coverHeight || 900}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="w-full h-auto"
            />
          </figure>
        )}

        <div className="article-body" dangerouslySetInnerHTML={{ __html: intro }} />

        {headings.length > 2 && (
          <nav
            data-testid="article-toc"
            aria-label={a.contents}
            className="my-9 rounded-2xl border border-[#E5E1D8] bg-[#FDFBF7] px-6 py-6 md:px-8"
          >
            <p className="text-[12px] uppercase tracking-[0.2em] text-sage-900/70 mb-4">{a.contents}</p>
            <ol className="space-y-2.5 text-[15px] leading-snug">
              {headings.map((h, i) => (
                <li key={h.id} className="flex gap-3">
                  <span aria-hidden="true" className="text-sage-900/30 tabular-nums">{i + 1}.</span>
                  <a
                    href={`#${h.id}`}
                    data-testid={`article-toc-link-${i}`}
                    className="text-sage-900/75 hover:text-sage transition-colors"
                  >
                    {h.text}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="article-body" dangerouslySetInnerHTML={{ __html: body }} />

        <aside
          data-testid="article-author-card"
          className="mt-14 rounded-2xl border border-[#E5E1D8] bg-[#FDFBF7] px-6 py-7 md:px-8 md:py-8"
        >
          <p className="text-[12px] uppercase tracking-[0.2em] text-sage-900/70 mb-5">{a.cardHeading}</p>
          <div className="flex flex-col sm:flex-row gap-6">
            <img
              src={PORTRAIT}
              alt={a.portraitAlt}
              width={96}
              height={96}
              loading="lazy"
              decoding="async"
              className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-full object-cover object-[center_20%]"
            />
            <div>
              <p className="font-serif text-[1.3rem] text-sage-900 leading-snug">{a.name}</p>
              <p className="text-[13.5px] text-sage-900/70 mb-3">{a.jobTitle}</p>
              <p className="text-[15px] leading-[1.75] text-sage-900/75 mb-4">{a.bio}</p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[14.5px] font-medium">
                <a
                  data-testid="article-author-about-link"
                  href={a.aboutHref}
                  className="inline-flex items-center gap-1.5 text-[#A4552F] hover:text-[#8F5236] transition-colors"
                >
                  {a.aboutLabel} <ChevronRight size={15} strokeWidth={1.9} />
                </a>
                <a
                  data-testid="article-author-linkedin-link"
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={lang === 'en' ? 'Ruxandra Sterbet on LinkedIn' : 'Ruxandra Sterbet pe LinkedIn'}
                  className="inline-flex items-center gap-1.5 text-sage-900/70 hover:text-sage-900 transition-colors"
                >
                  LinkedIn <ChevronRight size={15} strokeWidth={1.9} />
                </a>
              </div>
            </div>
          </div>
        </aside>

        {article.internalLinks?.length > 0 && (
          <nav className="mt-10 p-6 bg-white rounded-2xl border border-stone-100" data-testid="article-internal-links">
            <p className="text-sm uppercase tracking-widest text-sage mb-4 font-medium">
              {lang === 'ro' ? 'Citește mai departe' : 'Continue reading'}
            </p>
            <ul className="space-y-2">
              {article.internalLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="text-sage-900/80 hover:text-sage transition-colors inline-flex items-center gap-2">
                    <ChevronRight size={15} className="text-sage" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {related.length > 0 && (
          <section className="mt-12" data-testid="article-related">
            <h2 className="font-serif text-2xl text-sage-900 mb-6">
              {lang === 'ro' ? 'Articole similare' : 'Related articles'}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {related.map((item) => (
                <ArticleCard
                  key={item.id}
                  article={item}
                  language={item.language}
                  onClick={onSelectArticle ? () => onSelectArticle(item) : undefined}
                />
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 p-8 bg-sage/5 rounded-2xl text-center">
          {article.offer && (
            <div className="text-left mb-2">
              <OfferCard offer={article.offer} language={lang} variant="footer" />
            </div>
          )}
          <h2 className="font-serif text-xl text-sage-900 mb-3">
            {lang === 'ro'
              ? 'Ai întrebări sau vrei să programezi o ședință?'
              : 'Have questions or want to book a session?'}
          </h2>
          <p className="text-sage-900/70 mb-4">
            {lang === 'ro'
              ? 'Programează o întâlnire pentru a discuta despre nevoile tale.'
              : 'Book a meeting to discuss your needs.'}
          </p>
          <a
            data-testid="article-booking-cta"
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-warm-orange hover:bg-warm-orangeDark text-white px-6 py-3 rounded-full font-medium transition-all"
          >
            {lang === 'ro' ? 'Programează o ședință' : 'Book a session'}
            <ChevronRight size={18} />
          </a>
        </div>
      </article>
    </div>
  );
};
